import { FEATURE_FLAGS, RPC_ENDPOINT } from "@solomon-apps/config";
import {
    createYaasApp,
    ORG_AUTH_DISTRIBUTOR_CONSOLE_TERMS,
    setOrgAuthConsoleTerms
} from "@solomon-apps/shared";
import { IS_PRODUCTION_BUILD } from "@solomon-apps/shared/src/utils/environment";
import { ConnectionManager } from "@solomon-labs/solana-connection";
import React from "react";
import { Navigate } from "react-router-dom";

import { apiAccessOnly } from "./components/ApiAccessRoute";
import { distributorOnly } from "./components/DistributorRoute";
import { Layout } from "./components/Layout";
import { CLIENT_HOME_PATH } from "./constants/navigation";
import { AccountsPage } from "./pages/AccountsPage";
import { ApiKeysPage } from "./pages/ApiKeysPage";
import { CallbackPage } from "./pages/CallbackPage";
import { ClaimPage } from "./pages/ClaimPage";
import { CompliancePage } from "./pages/Compliance/CompliancePage";
import { DashboardPage } from "./pages/DashboardPage";
import { EditPositionPage } from "./pages/EditPositionPage";
import { ExplorationBadgesPage } from "./pages/ExplorationBadgesPage";
import { ExplorationPoliciesPage } from "./pages/ExplorationPoliciesPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { LoginPage } from "./pages/LoginPage";
import { OnboardingEntry } from "./pages/Onboarding/OnboardingEntry";
import { OrganizationPage } from "./pages/OrganizationPage";
import { PositionsPage } from "./pages/PositionsPage";
import { Policies as ProgramPoliciesPage } from "./pages/ProgramPolicies/Policies";
import { Programs as ProgramsPage } from "./pages/Programs/Programs";
import { ProgramsDashboardPage } from "./pages/ProgramsDashboardPage";
import { ReportingPage } from "./pages/ReportingPage";
import { RewardDestinationsPage } from "./pages/RewardDestinationsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { AddPositionShowcase } from "./pages/Showcase/AddPositionShowcase";
import {
    ProgramShowcaseBlock,
    ProgramStepsShowcase
} from "./pages/Showcase/ProgramStepsShowcase";
import { TeamAddMemberPage } from "./pages/TeamAddMemberPage";
import { TeamMemberPage } from "./pages/TeamMemberPage";
import { TeamPage } from "./pages/TeamPage";
import { registerSimMockLayer } from "./services/sim/register";
import { initSimSession } from "./services/sim/simSession";
import { initSimWallet } from "./services/sim/simWallet";

/* yaas-client needs Solana RPC access for: (a) the micro-transfer
 * fallback in wallet ownership verification, (b) Squads multisig
 * detection in AddPosition Step 2. `createYaasApp` deliberately
 * doesn't initialize ConnectionManager (shared with yaas-issuer,
 * which has no Solana need today), so we wire it here. */
ConnectionManager.setEndpoint(RPC_ENDPOINT);

// Sign-in and operating-context errors name the Distributor and Company
// Recipient roles, not the shared default Issuer / Client wording.
setOrgAuthConsoleTerms(ORG_AUTH_DISTRIBUTOR_CONSOLE_TERMS);

// SIM mode (Layers 2 and 3): when loaded with `?sim=1`, activate the shared E2E
// wallet harness so Step 2 verification works without a browser wallet, and
// sign the console in as the identity the Layer-1 data mock serves. Both must
// run before React renders — the wallet provider reads its global in its first
// render, and `AuthGuard` decides on its first render and drops the query
// string when it redirects. No-op + inert otherwise, and never initialized in
// production bundles.
if (!IS_PRODUCTION_BUILD) {
    initSimWallet();
    initSimSession();
    registerSimMockLayer();
}

/**
 * Landing route at `/`. When the client home is `/` (Portfolio flag on) this
 * renders the dashboard; otherwise it redirects to the home page (Positions
 * today), so the dashboard is unreachable by URL. The home path — and thus the
 * Portfolio flag — is resolved once in `CLIENT_HOME_PATH`.
 */
function IndexRoute(): React.ReactElement {
    return CLIENT_HOME_PATH === "/" ? (
        <DashboardPage />
    ) : (
        <Navigate to={CLIENT_HOME_PATH} replace />
    );
}

/**
 * YaaS Client Console entry point
 */
void (async (): Promise<void> => {
    await createYaasApp({
        appName: "client",
        routes: [
            { key: "dashboard", path: "/", component: IndexRoute },
            // The Distributor section's routes follow the Distributions
            // access level, as its nav does (DistributorRoute).
            {
                key: "accounts",
                path: "/accounts",
                component: distributorOnly(AccountsPage)
            },
            { key: "positions", path: "/positions", component: PositionsPage },
            {
                key: "reward-destinations",
                path: "/reward-destinations",
                component: RewardDestinationsPage
            },
            /* The add flow is a mode of the Reward Destinations
             * page (header stays, table swaps for the form), like
             * /positions/add. The route gives it a real URL. */
            {
                key: "add-reward-destination",
                path: "/reward-destinations/add",
                component: RewardDestinationsPage
            },
            /* The add-position walkthrough is not a page of its own — it's a
             * mode of the Positions page (it keeps the page header + key stats
             * above it). The route exists so the flow has a real URL: browser
             * Back leaves it, refresh keeps you in it, and it's linkable from
             * the Dashboard. */
            {
                key: "add-position",
                path: "/positions/add",
                component: PositionsPage
            },
            {
                key: "edit-position",
                path: "/positions/:positionUuid",
                component: EditPositionPage
            },
            { key: "reporting", path: "/reporting", component: ReportingPage },
            ...(FEATURE_FLAGS.clientComplianceSurface
                ? [
                      {
                          key: "compliance",
                          path: "/compliance",
                          component: CompliancePage
                      }
                  ]
                : []),
            {
                key: "policies",
                path: "/policies",
                component: distributorOnly(ProgramPoliciesPage)
            },
            {
                key: "programs-dashboard",
                path: "/programs-dashboard",
                component: distributorOnly(ProgramsDashboardPage)
            },
            {
                key: "programs",
                path: "/programs",
                component: distributorOnly(ProgramsPage)
            },
            {
                key: "exploration-badges",
                path: "/exploration/badges",
                component: ExplorationBadgesPage
            },
            {
                key: "exploration-policies",
                path: "/exploration/policies",
                component: ExplorationPoliciesPage
            },
            { key: "team", path: "/team", component: TeamPage },
            // Add Member, opened from +Add Member. Listed before the member
            // page so "new" never reads as a member uuid.
            {
                key: "team-new",
                path: "/team/new",
                component: TeamAddMemberPage
            },
            // A member's own page, opened from a Team table row.
            {
                key: "team-member",
                path: "/team/:memberUuid",
                component: TeamMemberPage
            },
            // API keys follow the API Access level, as their nav item does
            // (ApiAccessRoute).
            {
                key: "api-keys",
                path: "/api-keys",
                component: apiAccessOnly(ApiKeysPage)
            },
            { key: "settings", path: "/settings", component: SettingsPage },
            {
                key: "organization",
                path: "/organization",
                component: OrganizationPage
            },
            {
                key: "onboarding",
                path: "/onboarding",
                component: OnboardingEntry
            }
        ],
        unprotectedRoutes: [
            { key: "login", path: "/login", component: LoginPage },
            {
                key: "forgot-password",
                path: "/forgot-password",
                component: ForgotPasswordPage
            },
            { key: "callback", path: "/callback", component: CallbackPage },
            { key: "claim", path: "/claim", component: ClaimPage },
            // Design-QA showcase for the +New Program step blocks (the
            // @solomon-apps/shared/sim export). Unprotected + bare so the
            // blocks render without auth/app chrome. Dev bundles only.
            ...(IS_PRODUCTION_BUILD
                ? []
                : [
                      {
                          key: "showcase-programs",
                          path: "/showcase/programs",
                          component: ProgramStepsShowcase
                      },
                      {
                          key: "showcase-program-block",
                          path: "/showcase/programs/:blockId",
                          component: ProgramShowcaseBlock
                      },
                      // Every state of the Add a Position flow, side by side.
                      {
                          key: "showcase-add-position",
                          path: "/showcase/add-position",
                          component: AddPositionShowcase
                      }
                  ])
        ],
        components: {
            layout: Layout
        }
    });
})();
